import { useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

import { useReselector } from '@/utils/sharedHooks'
import { getUserIsAdmin } from '@/selectors/user.selectors'
import { useAnalyticsTrack, useAnalyticsView } from '@/features/amplitude'
import { Heading, HeadingVariants, TabbedView, TabPanels, TabPanel } from '@/ui'
import { mtc } from '@/styles'
import { styles } from '../styles'
import { PlansAndBillingCard } from '../plans-billing-card'
import { AccountTab } from './account'
import { SettingsTabLabels } from '../SettingsTabLabels'
import { TABS } from '../tabs'
import { useLegacyHashTab } from '../useLegacyHashTab'
import { FinancialInstitutionsTab } from './financial-institutions'
import { FinancialInstitutionsError } from './financial-institutions/FinancialInstitutionsError'
import { SyncStatusProvider } from './financial-institutions/SyncStatusContext'

export const SelfServiceSettings = () => {
  const pageView = useAnalyticsView()
  const track = useAnalyticsTrack()
  const [searchParams] = useSearchParams()
  const isAdmin = useReselector(getUserIsAdmin)
  const validIds = useMemo(
    () => (isAdmin ? [TABS.ACCOUNT] : Object.values(TABS)),
    [isAdmin]
  )
  const defaultTab = useLegacyHashTab(TABS.ACCOUNT, validIds)

  useEffect(() => {
    pageView('account')
  }, [pageView])

  useEffect(() => {
    const plaidStatementSource = searchParams.get('psSrc')
    if (plaidStatementSource) {
      track(`view settings from ${plaidStatementSource}`)
    }
  }, [track, searchParams])

  const isFinancialInstitutions =
    !isAdmin && searchParams.get('tab') === TABS.FINANCIAL_INSTITUTIONS

  return (
    <SyncStatusProvider enabled={isFinancialInstitutions}>
      <div className={mtc(styles())}>
        {isFinancialInstitutions && <FinancialInstitutionsError />}
        <Heading variant={HeadingVariants.H1}>Settings</Heading>
        <TabbedView defaultId={defaultTab} validIds={validIds}>
          <SettingsTabLabels isAdmin={isAdmin} defaultTab={defaultTab} />
          <TabPanels>
            <TabPanel id={TABS.ACCOUNT}>
              <AccountTab />
            </TabPanel>
            {!isAdmin && (
              <>
                <TabPanel id={TABS.FINANCIAL_INSTITUTIONS}>
                  <FinancialInstitutionsTab />
                </TabPanel>
                <TabPanel id={TABS.PLAN_AND_BILLING}>
                  <PlansAndBillingCard />
                </TabPanel>
              </>
            )}
          </TabPanels>
        </TabbedView>
      </div>
    </SyncStatusProvider>
  )
}
